import React, { useRef, useEffect, useState } from 'react';
import { X, Reply, MoreVertical, Paperclip, Clock, Send, Loader2, Sparkles } from 'lucide-react';
import EmailDropdown from './EmailDropdown';
import EmailAttachments from './EmailAttachments';
import AIReplyDraft from './AIReplyDraft';
import AIEmailShortcuts from './AIEmailShortcuts';

/**
 * Decode HTML entities (&#39; -> ', &amp; -> &, etc.)
 */
const decodeHtmlEntities = (str) => {
  if (!str) return str;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
};

/**
 * EmailBodyRenderer — renders HTML email in a sandboxed iframe like Gmail,
 * with an injected dark-mode stylesheet for clean typography.
 */
const EmailBodyRenderer = ({ html, text }) => {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(200);

  useEffect(() => {
    const frame = iframeRef.current;
    if (!frame) return;
    if (!html) return;

    const doc = frame.contentDocument || frame.contentWindow?.document;
    if (!doc) return;

    // Decode HTML entities and clean the content
    const decodedHtml = decodeHtmlEntities(html);
    const cleaned = decodedHtml
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // remove scripts only
      .replace(/<html[^>]*>|<\/html>/gi, '')                               // strip html tags but keep content
      .replace(/<meta[^>]+>/gi, '')                                        // strip meta tags
      .replace(/(<br\s*\/?>\s*){4,}/gi, '<br><br>');                       // collapse excessive br tags

    const page = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
  /* Base: white background — emails are designed for light backgrounds */
  html, body {
    background: #ffffff !important;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #111827;
    margin: 0; padding: 8px;
    word-break: break-word;
    overflow-wrap: break-word;
  }
  /* Constrain images */
  img { max-width: 100% !important; height: auto; }
  /* Links */
  a { color: #4f46e5; }
  /* Tables */
  table { border-collapse: collapse; max-width: 100%; }
  /* Quoted/reply text */
  .gmail_quote { color: #6b7280; font-size: 13px; }
  /* Keep email's hidden elements hidden (preheader text, etc.) */
  [style*="display:none"], [style*="display: none"] { display: none !important; }
</style>
</head>
<body>${cleaned}</body>
</html>`;

    doc.open();
    doc.write(page);
    doc.close();

    // Resize to content height after render
    const resize = () => {
      const body = frame.contentDocument?.body;
      if (body) setHeight(body.scrollHeight + 16);
    };
    // Give the browser time to paint, then measure
    const timer = setTimeout(resize, 80);
    return () => clearTimeout(timer);
  }, [html]);

  if (!html) {
    // Decode HTML entities in plain text as well
    const decodedText = decodeHtmlEntities(text || '');
    return (
      <p className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">{decodedText}</p>
    );
  }

  return (
    <div className="rounded-lg overflow-hidden" style={{ background: '#ffffff' }}>
      <iframe
        ref={iframeRef}
        title="email-body"
        sandbox="allow-same-origin"
        className="w-full border-0 block"
        style={{ height, minHeight: 80 }}
        scrolling="no"
      />
    </div>
  );
};

/**
 * EmailThreadHeader - Header section with reply/forward buttons
 */
const EmailThreadHeader = ({
  email,
  onClose,
  onReply,
  onReplyAll,
  onForward
}) => (
  <div className="px-6 py-4 border-b border-gray-400 bg-black flex-shrink-0">
    <div className="flex items-center gap-4">
      <button 
        onClick={onClose}
        className="p-2 hover:bg-gray-900 rounded-full transition-colors"
        data-testid="email-close-btn"
      >
        <X size={20} className="text-gray-400" />
      </button>
      <div className="flex-1">
        <h3 className="text-lg font-bold text-white">{decodeHtmlEntities(email.subject)}</h3>
        <p className="text-xs text-gray-400">{email.thread?.length || 1} message{(email.thread?.length || 1) > 1 ? 's' : ''} in thread</p>
      </div>
      <div className="flex items-center gap-2">
        <button 
          onClick={onReply}
          className="px-3 py-1.5 bg-black border border-gray-400 hover:bg-gray-900 text-white rounded-lg text-sm font-medium transition-all flex items-center gap-1"
          data-testid="email-reply-btn"
        >
          <Reply size={14} />
          Reply
        </button>
        <button 
          onClick={onReplyAll}
          className="px-3 py-1.5 bg-black border border-gray-400 hover:bg-gray-900 text-white rounded-lg text-sm font-medium transition-all"
          data-testid="email-reply-all-btn"
        >
          Reply All
        </button>
        <button 
          onClick={onForward}
          className="px-3 py-1.5 bg-black border border-gray-400 hover:bg-gray-900 text-white rounded-lg text-sm font-medium transition-all"
          data-testid="email-forward-btn"
        >
          Forward
        </button>
      </div>
    </div>
  </div>
);

/**
 * EmailMessageItem - Individual email message in thread
 */
const EmailMessageItem = ({
  msg,
  parsedMsg,
  partIdx,
  uniqueKey,
  isSentByMe,
  isDropdownOpen,
  onToggleDropdown,
  onAddToMeeting,
  onNewMeeting,
  onAddContact,
  onNewContact,
  onAddToFlight,
  onAddToFinances,
  onAddAttachmentToDocuments,
  onDownloadAttachment,
  onReply,
  onAddToMeetingSimple,
  onCloseDropdown
}) => {
  return (
    <div 
      className={`rounded-xl p-5 relative group transition-all ${
        isSentByMe 
          ? 'bg-black border border-gray-400 hover:bg-gray-900' 
          : parsedMsg.isQuoted
            ? 'bg-black/50 border border-gray-500 hover:bg-gray-900/50'
            : 'bg-black border border-gray-400 hover:bg-gray-900'
      }`}
    >
      {/* Quoted indicator */}
      {parsedMsg.isQuoted && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gray-500 rounded-l-xl"></div>
      )}
      
      {/* Message Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h4 className={`text-base font-bold ${isSentByMe ? 'text-purple-300' : 'text-white'}`}>
            {isSentByMe ? 'You' : decodeHtmlEntities(parsedMsg.from || msg.from)}
          </h4>
          {!isSentByMe && (parsedMsg.email || msg.email) && (
            <span className="text-sm text-gray-500">{decodeHtmlEntities(parsedMsg.email || msg.email)}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">{parsedMsg.time || msg.time}</span>
          {partIdx === 0 && (
            <button
              onClick={onToggleDropdown}
              className="p-1 hover:bg-gray-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical size={16} className="text-gray-400" />
            </button>
          )}
          
          {/* Dropdown Menu */}
          {partIdx === 0 && (
            <EmailDropdown
              isOpen={isDropdownOpen}
              onClose={onCloseDropdown}
              email={msg}
              onReply={onReply}
              onAddToMeeting={onAddToMeeting}
              onNewMeeting={onNewMeeting}
              onAddContact={onAddContact}
              onNewContact={onNewContact}
              onAddToFlight={onAddToFlight}
              onAddToFinances={onAddToFinances}
              onAddAttachmentToDocuments={onAddAttachmentToDocuments}
              attachments={msg.attachments || []}
              showMeetingAttachmentCount={true}
              position="right-4 top-12"
            />
          )}
        </div>
      </div>
    
      {/* To/Cc Recipients */}
      {partIdx === 0 && isSentByMe && msg.to && msg.to.length > 0 && (
        <p className="text-sm text-gray-500 mb-3">
          <span className="text-gray-400">To:</span> {Array.isArray(msg.to) ? msg.to.join(', ') : msg.to}
        </p>
      )}
      
      {/* Attachments */}
      {partIdx === 0 && (
        <EmailAttachments
          attachments={msg.attachments || []}
          emailId={msg.id}
          onDownload={onDownloadAttachment}
          className="mb-4"
        />
      )}

      {/* Email Body */}
      <div className="email-body-content mt-2">
        <EmailBodyRenderer
          html={(parsedMsg.isHtml || (parsedMsg.body && parsedMsg.body.includes('<'))) ? parsedMsg.body : null}
          text={parsedMsg.body || ''}
        />
      </div>
    </div>
  );
};

/**
 * EmailReplyInput - Reply input area with AI assistance
 */
const EmailReplyInput = ({
  replyInput,
  setReplyInput,
  showAIDraft,
  aiDraftText,
  generatingAIReply,
  sendingReply,
  onUseDraft,
  onRefreshDraft,
  onCloseDraft,
  onGenerateAIReply,
  onSendReply,
  onOpenSchedule
}) => (
  <div className="px-6 py-4 border-t border-gray-400/20 bg-black flex-shrink-0">
    {/* AI Reply Draft */}
    <AIReplyDraft
      isVisible={showAIDraft && !!aiDraftText}
      draftText={aiDraftText}
      onUse={onUseDraft}
      onRefresh={onRefreshDraft}
      onClose={onCloseDraft}
      isRefreshing={generatingAIReply}
      className="mb-3"
    />
    <div className="flex items-start gap-3">
      <button className="p-2 hover:bg-gray-900 rounded-full transition-colors mt-1">
        <Paperclip size={20} className="text-gray-400" />
      </button>
      <button 
        onClick={onOpenSchedule}
        className="p-2 hover:bg-gray-900 rounded-full transition-colors mt-1"
        title="Schedule email"
      >
        <Clock size={20} className="text-gray-400" />
      </button>
      <button 
        onClick={onGenerateAIReply}
        disabled={generatingAIReply}
        className="p-2 hover:bg-gray-900 rounded-full transition-colors mt-1"
        title="Generate AI Reply"
        data-testid="email-ai-reply-btn"
      >
        {generatingAIReply ? (
          <Loader2 size={20} className="text-purple-400 animate-spin" />
        ) : (
          <Sparkles size={20} className="text-purple-400" />
        )}
      </button>
      <textarea
        value={replyInput}
        onChange={(e) => setReplyInput(e.target.value)}
        placeholder="Type your reply..."
        rows="4"
        onInput={(e) => {
          e.target.style.height = 'auto';
          e.target.style.height = Math.min(e.target.scrollHeight, 300) + 'px';
        }}
        className="flex-1 px-4 py-3 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none overflow-y-auto"
        style={{ minHeight: '120px', maxHeight: '300px' }}
      />
      <button 
        onClick={onSendReply}
        disabled={sendingReply || !replyInput.trim()}
        className={`p-2.5 rounded-full transition-colors self-end ${
          sendingReply || !replyInput.trim()
            ? 'bg-gray-700 cursor-not-allowed'
            : 'bg-purple-600 hover:bg-purple-700'
        }`}
        title="Send reply"
        data-testid="email-send-reply-btn"
      >
        {sendingReply ? (
          <Loader2 size={18} className="text-white animate-spin" />
        ) : (
          <Send size={18} className="text-white" />
        )}
      </button>
    </div>
  </div>
);

/**
 * EmailThreadView - Full email thread view with messages and reply input
 */
const EmailThreadView = ({
  email,
  onClose,
  // Reply/Forward handlers
  onReply,
  onReplyAll,
  onForward,
  // Thread message handlers
  openDropdownKey,
  onToggleDropdown,
  onCloseDropdown,
  parseEmailThread,
  formatTime,
  handleAddToMeetingWithAttachments,
  handleNewMeetingWithAttachments,
  handleAddContactFromEmail,
  handleAddToFlight,
  handleAddToFinances,
  handleAddAttachmentToDocuments,
  handleDownloadAttachment,
  handleReplyEmail,
  handleAddToMeeting,
  // New contact modal handlers
  setNewContactName,
  setNewContactPhone,
  setNewContactEmail,
  setNewContactCompany,
  setNewContactRole,
  setNewContactCategory,
  setNewContactNotes,
  setOriginalConversationName,
  setShowAddContactModal,
  // Reply input state
  replyInput,
  setReplyInput,
  showAIDraft,
  aiDraftText,
  setAiDraftText,
  setShowAIDraft,
  generatingAIReply,
  setGeneratingAIReply,
  sendingReply,
  onSendReply,
  onOpenSchedule,
  // AI API
  aiEmailAPI,
  // AI Shortcuts props
  userId,
  onDraftCreated,
  onActionItemsExtracted,
  slackChannels = [],
  className = ''
}) => {
  if (!email) return null;

  // Format time helper with fallback
  const displayTime = (time) => {
    if (formatTime) return formatTime(time);
    return time || '';
  };

  return (
    <div className={`flex-1 flex flex-col bg-black overflow-hidden ${className}`}>
      {/* Thread Header */}
      <EmailThreadHeader
        email={email}
        onClose={onClose}
        onReply={onReply}
        onReplyAll={onReplyAll}
        onForward={onForward}
      />

      {/* AI Email Shortcuts Toolbar */}
      {AIEmailShortcuts && (
        <AIEmailShortcuts
          email={email}
          userId={userId}
          onDraftCreated={onDraftCreated}
          onActionItemsExtracted={onActionItemsExtracted}
          slackChannels={slackChannels}
        />
      )}

      {/* Thread Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {email.thread?.flatMap((msg, msgIdx) => {
          const parsedMessages = parseEmailThread(msg.body, msg.from, msg.email, msg.time);
          
          return parsedMessages.map((parsedMsg, partIdx) => {
            const isSentByMe = parsedMsg.from?.toLowerCase() === 'you' || 
                               parsedMsg.from?.toLowerCase()?.includes('creatrhq') ||
                               parsedMsg.email?.toLowerCase()?.includes('creatrhq') ||
                               (partIdx === 0 && (msg.from?.toLowerCase()?.includes('creatrhq') || msg.email?.toLowerCase()?.includes('creatrhq')));
            
            const uniqueKey = `${msg.id || msgIdx}-${partIdx}`;
            
            return (
              <EmailMessageItem
                key={uniqueKey}
                msg={msg}
                parsedMsg={{
                  ...parsedMsg, 
                  time: displayTime(msg.date || msg.rawDate || parsedMsg.time || msg.time || msg.timestamp)
                }}
                partIdx={partIdx}
                uniqueKey={uniqueKey}
                isSentByMe={isSentByMe}
                isDropdownOpen={openDropdownKey === uniqueKey}
                onToggleDropdown={() => onToggleDropdown(uniqueKey)}
                onAddToMeeting={() => handleAddToMeetingWithAttachments(msg, msg.attachments?.filter(a => !a.isInline) || [])}
                onNewMeeting={() => handleNewMeetingWithAttachments(msg, msg.attachments?.filter(a => !a.isInline) || [])}
                onAddContact={() => handleAddContactFromEmail(msg)}
                onNewContact={() => {
                  setNewContactName('');
                  setNewContactPhone('');
                  setNewContactEmail(msg.email || '');
                  setNewContactCompany('');
                  setNewContactRole('');
                  setNewContactCategory('Personal');
                  setNewContactNotes('');
                  setOriginalConversationName('');
                  setShowAddContactModal(true);
                }}
                onAddToFlight={() => handleAddToFlight(msg)}
                onAddToFinances={() => handleAddToFinances(msg)}
                onAddAttachmentToDocuments={handleAddAttachmentToDocuments}
                onDownloadAttachment={handleDownloadAttachment}
                onReply={() => handleReplyEmail(msg)}
                onAddToMeetingSimple={() => handleAddToMeeting({ name: email.from, phone: email.email, id: email.id })}
                onCloseDropdown={onCloseDropdown}
              />
            );
          });
        })}
      </div>

      {/* Reply Input */}
      <EmailReplyInput
        replyInput={replyInput}
        setReplyInput={setReplyInput}
        showAIDraft={showAIDraft}
        aiDraftText={aiDraftText}
        generatingAIReply={generatingAIReply}
        sendingReply={sendingReply}
        onUseDraft={() => {
          setReplyInput(aiDraftText);
          setShowAIDraft(false);
          setAiDraftText('');
        }}
        onRefreshDraft={async () => {
          if (!email || generatingAIReply) return;
          setGeneratingAIReply(true);
          try {
            const lastMessage = email.thread?.[email.thread.length - 1] || email;
            const emailBody = lastMessage.body || email.body || email.preview || '';
            const result = await aiEmailAPI.generateEmailReply({
              email_id: String(lastMessage.id || email.id || 'unknown'),
              subject: email.subject || 'No Subject',
              sender: lastMessage.email || email.email || 'unknown@email.com',
              sender_name: lastMessage.from || email.from || 'Unknown Sender',
              body: emailBody,
              reply_tone: 'professional'
            });
            if (result.reply) {
              setAiDraftText(result.reply);
            }
          } catch (err) {
            console.error('Failed to regenerate AI reply:', err);
          } finally {
            setGeneratingAIReply(false);
          }
        }}
        onCloseDraft={() => { setShowAIDraft(false); setAiDraftText(''); }}
        onGenerateAIReply={async () => {
          if (!email) return;
          setGeneratingAIReply(true);
          try {
            const lastMessage = email.thread?.[email.thread.length - 1] || email;
            const emailBody = lastMessage.body || email.body || email.preview || '';
            const result = await aiEmailAPI.generateEmailReply({
              email_id: String(lastMessage.id || email.id || 'unknown'),
              subject: email.subject || 'No Subject',
              sender: lastMessage.email || email.email || 'unknown@email.com',
              sender_name: lastMessage.from || email.from || 'Unknown Sender',
              body: emailBody,
              reply_tone: 'professional'
            });
            if (result.reply) {
              setAiDraftText(result.reply);
              setShowAIDraft(true);
            }
          } catch (err) {
            console.error('Failed to generate AI reply:', err);
            setAiDraftText('Thank you for your email. I appreciate the detailed information. I\'d like to schedule a meeting to discuss this further. Would Tuesday at 2 PM work for your team?');
            setShowAIDraft(true);
          } finally {
            setGeneratingAIReply(false);
          }
        }}
        onSendReply={onSendReply}
        onOpenSchedule={onOpenSchedule}
      />
    </div>
  );
};

export default EmailThreadView;
